import React, { useCallback } from 'react';

import { MicrodataHTMLAttributes, schema } from '@rover/components/src/microdata';
import { Flex } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import { withOpacity } from '@rover/kibble/utils';
import useOnIntersect from '@rover/react-lib/src/hooks/useOnIntersect';
import type { OptimizedSearchResult } from '@rover/rsdk/src/apiClient/latest/model/optimizedSearchResult';
import type { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';

import Card, { CardLink } from '../Card';

import ProfileNameAndPriceRow from './components/ProfileNameAndPriceRow';
import SitterStatsSection from './components/SitterStatsSection';
import StaticFeaturedReview from './components/StaticFeaturedReview';
import getReviewDetails from './utils/getReviewDetails';

export type Props = {
  language: string;
  searchResult: OptimizedSearchResult;
  preferCatReviews: boolean;
  priceTransparencyLevel?: PriceTransparencyLevel;
  onClick?: () => void;
  onViewIntersect?: (personOpk: OptimizedSearchResult['personOpk']) => void;
  rootCarouselRef?: React.RefObject<HTMLElement>;
  itemProp?: MicrodataHTMLAttributes['itemProp'];
};

const CarouselSitterCard: React.FC<Props> = ({
  language,
  searchResult,
  preferCatReviews,
  priceTransparencyLevel,
  onClick = () => {},
  onViewIntersect = () => {},
  rootCarouselRef,
  itemProp,
}: Props) => {
  const { colors } = useTheme();
  const {
    personOpk,
    description,
    shortName,
    webUrl,
    isFacility,
    reviewsCount,
    providerProfile,
    yearsOfExperience,
    ratingsAverage,
    businessAddress,
    neighborhood,
    city,
    state,
    zip,
  } = searchResult;
  const { reviewText, reviewPhotoUrl, reviewerShortName } = getReviewDetails(
    preferCatReviews,
    searchResult
  );
  const handleCardVisible = useCallback(() => {
    onViewIntersect(personOpk);
  }, [personOpk, onViewIntersect]);

  const cardRef = useOnIntersect(handleCardVisible, true, {
    root: rootCarouselRef?.current,
    threshold: 0.75,
  });

  return (
    <Flex
      htmlRef={cardRef}
      maxWidth={[300, 350]}
      minWidth={[300, 350]}
      width={[300, 350]}
      sx={{ boxShadow: `0px 1px 4px 0px ${withOpacity(colors.neutral['900'], 0.23)}` }}
      borderRadius="8px"
      position="relative"
      flexDirection="column"
      webProps={{
        itemScope: true,
        itemProp,
        itemType: isFacility ? schema`Organization` : schema`Person`,
      }}
      height="100%"
    >
      <meta itemProp="url" content={webUrl} />
      <CardLink
        aria-label={shortName}
        href={webUrl}
        rel="nofollow"
        target="_self"
        onClick={onClick}
      />
      <Card borderRadius="8px" background="primary" cardStyle="inset" isStacked padding="4x">
        <Flex flexDirection="column" gap="3x">
          <ProfileNameAndPriceRow
            searchResult={searchResult}
            language={language}
            priceTransparencyLevel={priceTransparencyLevel}
          />
          <SitterStatsSection
            reviewsCount={reviewsCount}
            repeatClientCount={providerProfile.repeatClientCount}
            yearsOfExperience={yearsOfExperience}
            ratingsAverage={ratingsAverage}
            businessAddress={businessAddress}
            neighborhood={neighborhood}
            city={city}
            state={state}
            zip={zip}
            isFacility={isFacility}
          />
        </Flex>
      </Card>
      <Card
        borderRadius="8px"
        cardStyle="inset"
        isStacked
        paddingX="5x"
        paddingY="4x"
        background="secondary"
        height="100%"
      >
        <StaticFeaturedReview
          reviewText={reviewText}
          reviewPhotoUrl={reviewPhotoUrl}
          reviewerShortName={reviewerShortName}
          description={description}
          defaultAvatar={preferCatReviews ? 'cat' : 'dog'}
        />
      </Card>
    </Flex>
  );
};
export default CarouselSitterCard;
